import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DeprecatedConfirmationDialog } from "@/components/ui/deprecated-confirmation-dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { Input, TextArea } from "@/components/ui/input"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { toast } from "@/hooks/useToast"
import { textAreaDefaultMaxLength } from "@/models/common"
import {
  GeneralAttestationForm,
  generalAttestationSchema,
} from "@/modules/patients/detail/general-attestation/de/schema/general-attestation"
import { deGeneralAttestationsFragment } from "@/modules/patients/detail/general-attestation/de/services/deGeneralAttestationsFragment"
import { useDeCreateGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeCreateGeneralAttestationMutation"
import { useDeDeleteGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeDeleteGeneralAttestationMutation"
import { useDeGenerateGeneralAttestationDocumentMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeGenerateGeneralAttestationDocumentMutation"
import { useDeUpdateGeneralAttestationMutation } from "@/modules/patients/detail/general-attestation/de/services/useDeUpdateGeneralAttestationMutation"
import { getPrefilledTranslations } from "@/modules/patients/detail/general-attestation/lib/get-prefilled-translations"
import { useGeneralAttestationContext } from "@/modules/patients/detail/general-attestation/store/useGeneralAttestationStore"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

const DeGeneralAttestationDrawer = withErrorBoundary(() => {
  const { t } = useTranslation()
  const patientId = usePatientParam()
  const progressNoteId = useGeneralAttestationContext((state) => state.progressNoteId)
  const generalAttestationId = useGeneralAttestationContext((state) => state.generalAttestationId)
  const setGeneralAttestationId = useGeneralAttestationContext((state) => state.setGeneralAttestationId)
  const generalAttestationRef = useGeneralAttestationContext((state) => state.generalAttestationRef)
  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const isEditMode = !!generalAttestationId
  const isAddMode = !isEditMode && patientId && progressNoteId
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false)
  const [createGeneralAttestation, isCreating] = useDeCreateGeneralAttestationMutation()
  const [deleteGeneralAttestation] = useDeDeleteGeneralAttestationMutation()
  const [updateGeneralAttestation, isUpdating] = useDeUpdateGeneralAttestationMutation()
  const [generateGeneralAttestationDocument, isGenerating] = useDeGenerateGeneralAttestationDocumentMutation()
  const persistedData = useFragment(deGeneralAttestationsFragment, generalAttestationRef)
  const updatedAt = persistedData?.updatedAt || persistedData?.createdAt
  const updatedBy = persistedData?.updatedBy || persistedData?.createdBy
  const parsedData = isEditMode
    ? generalAttestationSchema.parse({
        ...persistedData,
        to: persistedData?.name ?? "",
      })
    : null

  const {
    handleSubmit,
    register,
    formState: { errors: formErrors, isDirty },
  } = useForm<GeneralAttestationForm>({
    mode: "all",
    resolver: zodResolver(generalAttestationSchema),
    defaultValues: parsedData
      ? {
          ...parsedData,
        }
      : {
          ...getPrefilledTranslations(),
        },
  })
  useDirtyDrawerMaker(isDirty)

  const handleGenerateDocument = (id: string) => {
    if (id && progressNoteId) {
      generateGeneralAttestationDocument({
        params: {
          id,
        },
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }
  }

  const onSubmit = (formData: GeneralAttestationForm) => {
    if (!progressNoteId) return
    const { to, message, subject } = formData

    if (isAddMode)
      createGeneralAttestation({
        variables: {
          input: {
            message,
            name: to,
            subject,
            patientId,
            progressNoteId,
          },
        },
        onCompleted(response) {
          const generalAttestationId = response?.de_createGeneralAttestation?.de_GeneralAttestation?.id

          if (generalAttestationId) {
            handleGenerateDocument(generalAttestationId)
          }
        },
        updater(store, data) {
          try {
            const progressNoteRecordProxy = store.get(progressNoteId)
            const generalAttestation = data?.de_createGeneralAttestation?.de_GeneralAttestation

            if (generalAttestation?.id) {
              const newRecordProxy = store.get(generalAttestation.id)
              if (newRecordProxy) {
                progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "de_generalAttestation")
              }
            }
          } catch (error) {
            console.error("Error while processing data:", error)
          }
        },
      })

    if (isEditMode) {
      updateGeneralAttestation({
        variables: {
          input: {
            message,
            name: to,
            subject,
            id: generalAttestationId,
          },
        },
        onCompleted(response) {
          const generalAttestationId = response.de_updateGeneralAttestation.de_GeneralAttestation?.id

          if (generalAttestationId) {
            handleGenerateDocument(generalAttestationId)
          }
        },
      })
    }
  }

  const handleCloseDrawer = () => {
    setGeneralAttestationId(null, null)
    closeDrawer()
    FaroInsightsWrapper.pushEvent("documents_GeneralAttestation_close")
  }

  const handleOpenConfirmDialog = (isOpen: boolean) => {
    setIsOpenConfirmDialog(isOpen)
  }

  const handleDelete = () => {
    if (progressNoteId && generalAttestationId) {
      deleteGeneralAttestation({
        variables: {
          input: {
            id: generalAttestationId,
          },
        },
        onCompleted() {
          handleCloseDrawer()
          handleOpenConfirmDialog(false)
          toast({
            description: t("patient.generalAttestation.deleteMessage.success"),
          })
        },
      })
    }
  }

  return (
    <>
      <Drawer open onOpenChange={handleCloseDrawer}>
        <DrawerContent>
          <DrawerClose className="top-2 right-2" />
          <DrawerHeader className="pb-4" data-testid="de-general-attestation-header">
            <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
              {t("episodeOfCare.progressNote.generalAttestation")}
            </DrawerTitle>
          </DrawerHeader>
          <DrawerBody data-testid="de-general-attestation-body">
            <div className="flex flex-col gap-8">
              <div className="flex gap-4">
                <Input
                  {...register("to")}
                  data-testid="general-attestation-to-input"
                  error={formErrors.to?.message?.toString()}
                  translateError={true}
                  label={t("patient.generalAttestation.to")}
                  required
                />
              </div>
            </div>
            <div className="flex flex-col gap-4 pt-8.75">
              <label className="text-on-surface text-base font-medium">{t("patient.generalAttestation.message")}</label>
              <div>
                <Input
                  {...register("subject")}
                  data-testid="general-attestation-subject-input"
                  error={formErrors.subject?.message?.toString()}
                  translateError={true}
                  label={t("patient.generalAttestation.subject")}
                  required
                />
              </div>
              <div>
                <TextArea
                  {...register("message")}
                  data-testid="general-attestation-message"
                  error={formErrors.message?.message}
                  label={t("patient.generalAttestation.message")}
                  required
                  translateError
                  textLimit={textAreaDefaultMaxLength}
                />
              </div>
            </div>
          </DrawerBody>
          <DrawerFooter className="flex-wrap gap-2" data-testid="general-attestation-footer">
            {isEditMode && (
              <div className="flex gap-2">
                {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                <button
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_GeneralAttestation_delete")
                    handleOpenConfirmDialog(true)
                  }}
                  data-testid="general-attestation-delete-button">
                  <TrashSimple className="text-on-surface-subdued" />
                </button>
              </div>
            )}

            <div className="ml-auto flex gap-2">
              <Button variant="secondary" data-testid="general-attestation-cancel-button" onClick={handleCloseDrawer}>
                {t("common.button.cancel")}
              </Button>
              <Button
                disabled={isEditMode && !isDirty}
                onClick={() => {
                  FaroInsightsWrapper.pushEvent("documents_generalAttestation_save")
                  handleSubmit(onSubmit)()
                }}
                isLoading={isCreating || isUpdating || isGenerating}
                data-testid="general-attestation-save-button">
                {t("common.button.save")}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <DeprecatedConfirmationDialog
        title={t("patient.generalAttestation.deleteConfirmTitle")}
        content={t("episodeOfCare.deleteProgressNote.content")}
        open={isOpenConfirmDialog}
        onConfirm={handleDelete}
        confirmVariant="danger"
        confirmText={t("common.button.delete")}
        cancelText={t("common.button.keep")}
        onOpenChange={handleOpenConfirmDialog}
      />
    </>
  )
}, "DeGeneralAttestationDrawer")

export { DeGeneralAttestationDrawer }
